import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Plus, Trash2, Save, Eye, EyeOff, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Evidence", "Ratings", "Strategy", "PACT Act", "TDIU", "Filing", "General"];

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

export default function ArticleManager() {
  const { user } = useAuth();
  const [articles, setArticles] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/articles");
    setArticles(data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ title: "", slug: "", excerpt: "", body: "", category: "Evidence", cover_url: "", published: false });
  const startEdit = (a) => setEditing({ ...a });

  const remove = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    await api.delete(`/articles/${id}`);
    toast.success("Deleted");
    load();
  };

  const save = async () => {
    if (!editing.title || !editing.body) return toast.error("Title and body are required.");
    if (!editing.slug) editing.slug = slugify(editing.title);
    setSaving(true);
    try {
      if (editing.article_id) {
        await api.patch(`/articles/${editing.article_id}`, editing);
      } else {
        await api.post("/articles", { ...editing, excerpt: editing.excerpt || editing.body.slice(0, 160) });
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const togglePublish = async (a) => {
    await api.patch(`/articles/${a.article_id}`, { published: !a.published });
    load();
  };

  if (!user || user.role !== "admin") return <div className="p-24 text-center">Admin only.</div>;
  if (!articles) return <div className="grid place-items-center h-96"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-10">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <Link to="/admin" className="text-sm text-primary/60 hover:text-primary font-semibold" data-testid="articles-back">← Back to Admin</Link>
          <div className="label-caps text-primary/70 mt-4">Admin, Content</div>
          <h1 className="font-display text-3xl lg:text-5xl mt-1">Learn articles</h1>
        </div>
        <Button onClick={startNew} size="lg" data-testid="new-article"><Plus className="w-4 h-4" /> New article</Button>
      </div>

      {editing ? (
        <Card className="p-8 border-2 mb-8" data-testid="article-editor">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl">{editing.article_id ? "Edit article" : "New article"}</h2>
            <button onClick={() => setEditing(null)} className="text-primary/60 hover:text-primary" data-testid="editor-close"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <Label>Title</Label>
              <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: editing.slug || slugify(e.target.value) })} className="mt-2 h-12" data-testid="editor-title" />
            </div>
            <div>
              <Label>Slug (URL)</Label>
              <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })} className="mt-2 h-12" data-testid="editor-slug" />
            </div>
            <div>
              <Label>Category</Label>
              <select value={editing.category || "Evidence"} onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                className="mt-2 block w-full h-12 px-3 border-2 border-input bg-white font-semibold text-sm" data-testid="editor-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label>Cover image URL</Label>
              <Input value={editing.cover_url || ""} onChange={(e) => setEditing({ ...editing, cover_url: e.target.value })} className="mt-2 h-12" placeholder="https://…" data-testid="editor-cover" />
            </div>
            <div className="md:col-span-2">
              <Label>Excerpt (shown on Learn hub cards)</Label>
              <Textarea rows={2} value={editing.excerpt || ""} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className="mt-2" data-testid="editor-excerpt" />
            </div>
            <div className="md:col-span-2">
              <Label>Body (plain text with blank lines for paragraphs)</Label>
              <Textarea rows={16} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} className="mt-2 font-mono text-sm" data-testid="editor-body" />
            </div>
          </div>
          <div className="mt-6 flex items-center justify-between border-t border-border pt-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="editor-publish" />
              <span className="text-sm font-semibold">Published (visible on the public Learn hub)</span>
            </label>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="editor-save"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}</Button>
            </div>
          </div>
        </Card>
      ) : null}

      <Card className="p-0 border-2 overflow-x-auto">
        <table className="min-w-[900px] w-full text-sm" data-testid="articles-table">
          <thead className="bg-secondary">
            <tr>
              <th className="text-left p-3 font-semibold">Title</th>
              <th className="text-left p-3 font-semibold">Category</th>
              <th className="text-left p-3 font-semibold">Slug</th>
              <th className="text-left p-3 font-semibold">Status</th>
              <th className="text-left p-3 font-semibold">Created</th>
              <th className="text-center p-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {articles.map((a) => (
              <tr key={a.article_id} className="border-t border-border hover:bg-secondary/40" data-testid={`article-row-${a.slug}`}>
                <td className="p-3 font-semibold">{a.title}</td>
                <td className="p-3"><span className="px-2 py-1 text-xs bg-secondary border">{a.category || "General"}</span></td>
                <td className="p-3 font-mono text-xs text-primary/60">/learn/{a.slug}</td>
                <td className="p-3">
                  <button onClick={() => togglePublish(a)} className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 border-2 ${a.published ? "bg-primary text-primary-foreground border-primary" : "bg-white text-primary border-border"}`} data-testid={`toggle-${a.slug}`}>
                    {a.published ? <><Eye className="w-3 h-3" /> Published</> : <><EyeOff className="w-3 h-3" /> Draft</>}
                  </button>
                </td>
                <td className="p-3 text-xs text-primary/60">{new Date(a.created_at).toLocaleDateString()}</td>
                <td className="p-3">
                  <div className="flex gap-2 justify-center">
                    <Button variant="outline" size="sm" onClick={() => startEdit(a)} data-testid={`edit-${a.slug}`}>Edit</Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(a.article_id)} data-testid={`delete-${a.slug}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {articles.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-primary/50">No articles yet. Create one to get started.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
